import { Link } from 'react-router';
import type {
    ProfileNavigationItemProps,
    ShorthandCollection,
} from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import { CheckboxBlankLine, CheckboxFill } from '@archipro-design/icons';
import { generateURL } from '@archipro-website/top-navigation';
import { SEARCH_CATEGORIES, SEARCH_QUERY_KEY } from '../config';
import type { SearchIndex } from '../type';
import type { useSearchFilters } from '~/modules/search/hook/use-search-filters';

interface UseMobileProfileNavigationItemsProps {
    selectedCategory?: SearchIndex;
    searchQuery: string;
    basePath: string;
    searchFilters: ReturnType<typeof useSearchFilters>;
}

const createNavigationFilters = (
    searchFilters: ReturnType<typeof useSearchFilters>,
    items: ShorthandCollection<ProfileNavigationItemProps>,
) => {
    const { filterBarFilters, updateFilter } = searchFilters;
    filterBarFilters?.forEach(
        ({ filterKey, label, checked, disabled, props, newState }) => {
            items.push({
                key: `${filterKey}-tab`,
                iconBefore: checked ? <CheckboxFill /> : <CheckboxBlankLine />,
                children: label,
                onClick: () => updateFilter(filterKey, newState),
                selected: false,
                transparent: true,
                outlined: true,
                disabled,
                className: props.className,
            });
        },
    );
    return items;
};

const useMobileProfileNavigationItems = ({
    selectedCategory,
    searchQuery,
    basePath,
    searchFilters,
}: UseMobileProfileNavigationItemsProps) => {
    const items: ShorthandCollection<ProfileNavigationItemProps> = [];

    SEARCH_CATEGORIES.forEach(
        ({ children, mobileName, category, index }, itemIndex) => {
            const url = generateURL(basePath, {
                category: category ?? '',
                [SEARCH_QUERY_KEY]: searchQuery,
            });
            const selected = selectedCategory === index;
            if (selectedCategory === index) {
                createNavigationFilters(searchFilters, items);
            }
            items.push({
                key: `${children}-tab`,
                as: Link,
                selected,
                to: url,
                prefetch: 'intent',
                children: mobileName ?? children,
                styles: {
                    marginRight:
                        itemIndex + 1 === SEARCH_CATEGORIES.length
                            ? pxToRem(18)
                            : '',
                },
            });
        },
    );
    return { items };
};

export { useMobileProfileNavigationItems, createNavigationFilters };
